import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, FileText, AlertCircle } from 'lucide-react';
import { authAxios } from '../../services/crmApi';

interface ChecklistItem {
  key: string;
  label: string;
  description?: string;
  uploaded: boolean;
  documentId?: number;
}

interface ChecklistData {
  required: ChecklistItem[];
  completed: number;
  total: number;
  percentage: number;
  missing: string[];
}

interface Props {
  applicationId: number;
  accentColor?: string;
}

export const DocumentChecklistWidget: React.FC<Props> = ({ applicationId, accentColor = 'blue' }) => {
  const [data, setData] = useState<ChecklistData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    authAxios.get(`/api/crm/applications/${applicationId}/checklist`)
      .then(res => {
        if (mounted) setData(res.data);
      })
      .catch(() => {
        if (mounted) setData(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [applicationId]);

  if (loading) return null;
  if (!data || data.total === 0) return null;

  const pct = data.percentage;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <FileText size={14} className={`text-${accentColor}-500`} />
          <span className="text-xs font-semibold text-gray-700">Checklist dokumentów</span>
        </div>
        <span className={`text-[10px] font-bold ${pct === 100 ? 'text-emerald-600' : pct >= 50 ? `text-${accentColor}-600` : 'text-amber-600'}`}>
          {data.completed}/{data.total} ({pct}%)
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-gray-100 rounded-full mb-2.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? `bg-${accentColor}-500` : 'bg-amber-500'}`}
          style={{ width: `${Math.max(pct, 3)}%` }}
        />
      </div>

      {/* Items */}
      <div className="space-y-1">
        {data.required.map(item => (
          <div key={item.key} className="flex items-center gap-1.5 py-0.5">
            {item.uploaded ? (
              <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
            ) : (
              <Circle size={12} className="text-gray-300 shrink-0" />
            )}
            <span className={`text-[11px] ${item.uploaded ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      {/* Missing alert */}
      {data.missing.length > 0 && (
        <div className="mt-2 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 rounded p-1.5">
          <AlertCircle size={10} className="mt-0.5 shrink-0" />
          <span>Brakuje {data.missing.length} {data.missing.length === 1 ? 'dokumentu' : 'dokumentów'}</span>
        </div>
      )}
    </div>
  );
};
